/**
 * Why a player is opening at its initial position — declared by the host,
 * since only the host knows whether a start position is a deliberate seek,
 * a live entry, or a saved replay position (the player itself cannot tell
 * these apart from a bare UTC timestamp).
 *
 *   live      this is the device's current live session — open at the live
 *             edge; any saved resume position is ignored.
 *   resume    reopen where the viewer left off (or the start, if nothing
 *             was saved) — a picked/idle/stage VOD session, or replay mode.
 *   explicit  a specific moment was asked for: a lap jump, a deep link
 *             (e.g. a share token), or an operator/synced-viewer seek. A
 *             target outside the currently available window lands at the
 *             nearest edge instead of snapping all the way to live.
 */
export type EntryIntent = 'live' | 'resume' | 'explicit';

/**
 * Precedence a host applies to pick its entry intent: an explicit seek
 * (lap jump, deep link, operator/synced seek) always wins; otherwise the
 * device's own CURRENT live session opens at the live edge; anything else
 * (a picked session, a stage/idle-device VOD fallback, replay mode) resumes
 * from its saved position.
 */
export function resolveEntryIntent(opts: {
  hasExplicitSeek: boolean;
  isDeviceLiveSession: boolean;
}): EntryIntent {
  if (opts.hasExplicitSeek) return 'explicit';
  return opts.isDeviceLiveSession ? 'live' : 'resume';
}

/** The per-seek clamp intent (`seekClamp.ts`) an entry-point seek should use
 *  for a given entry intent. `live` never reaches this — its host contract
 *  is to pass a null `initialSeekUtcMs` and let the player place itself. */
export function entrySeekClampIntent(entryIntent: EntryIntent): 'explicit' | 'resume' {
  return entryIntent === 'explicit' ? 'explicit' : 'resume';
}
